import {safeParseOrElse} from '@/utilities/safe-parse-or-else';
import {Signal} from '@preact/signals';
import register from 'preact-custom-element';
import {useMemo} from 'preact/hooks';
import {z} from 'zod';
import SellingPlanSelector from './SellingPlanSelector';

const sellingPlansSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
    options: z.array(z.object({value: z.string()})),
    price_adjustments: z.array(z.object({value: z.number()})),
  })
);

type VariantSignal = Signal<{
  options: string[];
  selling_plan_allocations: {
    selling_plan_id: number;
  }[];
} | null>;

/**
 * Wrapper around `SellingPlanSelector` which implements the business logic for the flat buy-stack.
 * @param sellingPlans - JSON string of the selling plans available for the product
 */
function SellingPlanSelectorRegister({
  formId,
  sellingPlans: sellingPlansJson,
  selectedVariant,
}: {
  formId?: string;
  sellingPlans: string;
  selectedVariant: VariantSignal;
}) {
  const sellingPlans = useMemo(
    () => {
      const allPlans = safeParseOrElse(
        sellingPlansSchema,
        [],
        JSON.parse(sellingPlansJson)
      );
      const filteredPlans = allPlans.filter((plan) =>
        selectedVariant.value?.selling_plan_allocations.some(
          (a) => a.selling_plan_id === plan.id
        )
      );
      filteredPlans.sort(
        (a, b) => parseInt(a.options[1].value) - parseInt(b.options[1].value)
      );

      return filteredPlans;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sellingPlansJson, selectedVariant.value]
  );

  // The default plan is the most frequent plan that is less than or equal to the pack size.
  // This encourages users to subscribe for ~1 pack per month
  const defaultPlan = useMemo(() => {
    const size = parseInt(selectedVariant.value?.options[1] ?? '1');
    // In case the size is over 60, we should assume it refers to 'loads' rather than 'packs'
    // In sheets products, are 60 loads per pack
    const packSize = size >= 60 ? Math.floor(size / 60) : size;
    const plansReversed = [...sellingPlans].reverse();
    return (
      plansReversed.find((plan) => {
        const interval = parseInt(plan.options[1]?.value ?? '1');
        return interval <= packSize;
      }) ?? sellingPlans[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant.value?.options[1], sellingPlans]);

  if (sellingPlans.length === 0) {
    return <div></div>;
  }

  return (
    <SellingPlanSelector
      formId={formId}
      sellingPlans={sellingPlans}
      defaultPlan={defaultPlan}
    />
  );
}

export function registerSellingPlanSelector(selectedVariant: VariantSignal) {
  register(
    ({sellingPlans}: {sellingPlans: string}) => (
      <SellingPlanSelectorRegister
        sellingPlans={sellingPlans}
        selectedVariant={selectedVariant}
      />
    ),
    'selling-plan-selector'
  );
}
